/**
 * WP-7 save/resume. One slot in localStorage holding the serialized GameState
 * plus the small bits of UI state the player would miss on reload (the current
 * selection and the event log). Because the RNG lives inside G (rules/state.ts),
 * a restored game replays identically — the resume E2E asserts deep-equal G.
 *
 * UI-layer only: localStorage and try/catch live here so the rest of the game
 * never touches them. Saving is best-effort (private-mode / quota failures are
 * swallowed) — the game stays playable without a save.
 */
import { deserialize, serialize, type GameState } from '../rules/state';
import { newCampaign, type Campaign } from '../rules/campaign';

const KEY = 'ar_save_v1';

/** One combat/event/move line in the play-screen log. Move entries carry the
 *  turn they were made on + the traversed hex path, so a move can be re-selected
 *  (and its dotted trail drawn) while it's still the current turn. Build entries
 *  carry their turn too, so the end-turn confirmation can list this turn's orders. */
export interface LogEntry {
  html: string;
  move?: { turn: number; path: [number, number][] };
  build?: { turn: number };
}

interface SaveBlob {
  v: 1;
  g: string;
  selectedId: number | null;
  log: (LogEntry | string)[]; // older saves stored plain strings
  level?: number;   // AI difficulty 1–11 — for the title "Resume" subtitle (older saves lack it)
  size?: string;    // board size ('small'|'medium'|'large') — for the "{Size}, {Admiral}" descriptor
  savedAt?: number; // Date.now() at save time — ditto
}

export interface Restored {
  g: GameState;
  selectedId: number | null;
  log: LogEntry[];
}

export function saveGame(g: GameState, selectedId: number | null, log: LogEntry[], level: number, size: string): void {
  try {
    const blob: SaveBlob = { v: 1, g: serialize(g), selectedId, log, level, size, savedAt: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(blob));
  } catch {
    /* private mode / quota — saving is best-effort */
  }
}

export interface SaveMeta {
  level: number;   // AI difficulty 1–11 (0 if an older save predates the field)
  size: string;    // board size ('' if an older save predates the field)
  turn: number;    // clamped to maxTurn (never the terminal turn marker)
  maxTurn: number;
  savedAt: number; // epoch ms (0 if an older save predates the field)
}

/** Lightweight metadata about the autosave for the title "Resume Battle" subtitle. */
export function savedMeta(): SaveMeta | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const blob = JSON.parse(raw) as SaveBlob;
    if (blob.v !== 1) return null;
    const g = deserialize(blob.g);
    return { level: blob.level ?? 0, size: blob.size ?? '', turn: Math.min(g.turn, g.maxTurn), maxTurn: g.maxTurn, savedAt: blob.savedAt ?? 0 };
  } catch {
    return null;
  }
}

export function loadGame(): Restored | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const blob = JSON.parse(raw) as SaveBlob;
    if (blob.v !== 1) {
      clearSave(); // stale/unknown version → drop it so we boot clean next time
      return null;
    }
    // normalise older saves that stored the log as plain HTML strings
    const log: LogEntry[] = (blob.log ?? []).map((e) => (typeof e === 'string' ? { html: e } : e));
    return { g: deserialize(blob.g), selectedId: blob.selectedId ?? null, log };
  } catch {
    // a corrupt blob would otherwise fail every boot — clear it once.
    clearSave();
    return null;
  }
}

export function clearSave(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

export function hasSave(): boolean {
  try {
    return localStorage.getItem(KEY) != null;
  } catch {
    return false;
  }
}

// ---- settings ----
const SKIP_CINE_KEY = 'ar_skip_cinematic';

/** Player preference: skip the WP-8 combat cinematic and resolve battles instantly. */
export function getSkipCinematic(): boolean {
  try {
    return localStorage.getItem(SKIP_CINE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setSkipCinematic(on: boolean): void {
  try {
    if (on) localStorage.setItem(SKIP_CINE_KEY, '1');
    else localStorage.removeItem(SKIP_CINE_KEY);
  } catch {
    /* ignore */
  }
}

// ---- campaign progress (its own slot, separate from the active battle) ----
const CAMPAIGN_KEY = 'ar_campaign_v1';

/** Load saved "Save the Admiral" progress, or a fresh campaign if none/invalid. */
export function loadCampaign(): Campaign {
  try {
    const raw = localStorage.getItem(CAMPAIGN_KEY);
    if (!raw) return newCampaign();
    const c = JSON.parse(raw) as Partial<Campaign>;
    if (c && typeof c === 'object' && c.swayed && c.res) {
      return { swayed: c.swayed, res: c.res, points: Number(c.points) || 0 };
    }
  } catch {
    /* corrupt / private mode → start fresh */
  }
  return newCampaign();
}

export function saveCampaign(c: Campaign): void {
  try {
    localStorage.setItem(CAMPAIGN_KEY, JSON.stringify(c));
  } catch {
    /* best-effort */
  }
}

export function clearCampaign(): void {
  try {
    localStorage.removeItem(CAMPAIGN_KEY);
  } catch {
    /* ignore */
  }
}

const VOL_KEY = 'ar_volume';
const MUTE_KEY = 'ar_muted';

/** Master volume 0..1 (default 0.8). */
export function getVolume(): number {
  try {
    const raw = localStorage.getItem(VOL_KEY);
    if (raw == null) return 0.8; // unset → default (Number(null) would be a silent 0)
    const v = Number(raw);
    return Number.isFinite(v) && v >= 0 && v <= 1 ? v : 0.8;
  } catch {
    return 0.8;
  }
}

export function setVolume(v: number): void {
  try {
    localStorage.setItem(VOL_KEY, String(Math.max(0, Math.min(1, v))));
  } catch {
    /* ignore */
  }
}

export function getMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setMuted(on: boolean): void {
  try {
    if (on) localStorage.setItem(MUTE_KEY, '1');
    else localStorage.removeItem(MUTE_KEY);
  } catch {
    /* ignore */
  }
}

// ---- WP-12 polish settings: CRT shader, default difficulty, F/S counter ----
const CRT_KEY = 'ar_crt';
const DIFF_KEY = 'ar_difficulty';
const FPS_KEY = 'ar_fps';

/** Retro CRT-shader overlay (scanlines + vignette). Default off. */
export function getCrt(): boolean {
  try {
    return localStorage.getItem(CRT_KEY) === '1';
  } catch {
    return false;
  }
}

export function setCrt(on: boolean): void {
  try {
    if (on) localStorage.setItem(CRT_KEY, '1');
    else localStorage.removeItem(CRT_KEY);
  } catch {
    /* ignore */
  }
}

/** Preferred AI difficulty (1–11) the buy/quick screens open on. Default 6. */
export function getDefaultDifficulty(): number {
  try {
    const raw = localStorage.getItem(DIFF_KEY);
    if (raw == null) return 6;
    const v = Math.round(Number(raw));
    return Number.isFinite(v) && v >= 1 && v <= 11 ? v : 6;
  } catch {
    return 6;
  }
}

export function setDefaultDifficulty(level: number): void {
  try {
    const v = Math.max(1, Math.min(11, Math.round(level)));
    localStorage.setItem(DIFF_KEY, String(v));
  } catch {
    /* ignore */
  }
}

/** The loving-homage F/S (frames-per-second) counter. Default off. */
export function getFps(): boolean {
  try {
    return localStorage.getItem(FPS_KEY) === '1';
  } catch {
    return false;
  }
}

export function setFps(on: boolean): void {
  try {
    if (on) localStorage.setItem(FPS_KEY, '1');
    else localStorage.removeItem(FPS_KEY);
  } catch {
    /* ignore */
  }
}

// ---- clear everything ----
/** Wipe ALL persisted game data — the current save, campaign progress, and every
 *  setting (volume/mute/CRT/FPS/difficulty/skip-cinematic). Every key this game
 *  owns is `ar_`-prefixed, so we drop those and leave any unrelated origin data
 *  alone. Callers reload afterwards so the app boots from defaults. */
export function clearAllData(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('ar_')) keys.push(k);
    }
    for (const k of keys) localStorage.removeItem(k);
  } catch {
    /* ignore — private mode / quota */
  }
}
